import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LANDING, LOGIN, VISA_AUTH } from "../../constants/ROUTER_PATH_TITLE";
import useAuth from "../../hooks/useAuth";
import { Button } from "@tripian/react";
import useTranslate from "../../hooks/useTranslate";
import { removeLocalStorageToken } from "../../App/AppWrapper/localStorages";
import * as gtag from "../../gtag";
import { trackEvent } from "../../tracking/eventTracking";
import VisaSteps from "../../components/VisaSteps/VisaSteps";
import classes from "./VisaAuthRedirect.module.scss";

const MODAL_LABEL = "visa_verification_modal";
const MODAL_SEEN_STORAGE_KEY = "visa_verification_modal_seen";

// Marks whether this browser saw the verification modal before, so a user who
// registered earlier but never completed verification is distinguishable.
const readAndMarkModalSeen = (): boolean => {
  try {
    const seen = localStorage.getItem(MODAL_SEEN_STORAGE_KEY) !== null;
    if (!seen) localStorage.setItem(MODAL_SEEN_STORAGE_KEY, new Date().toISOString());
    return seen;
  } catch {
    return false;
  }
};

const VisaAuthRedirect = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslate();

  const useQuery = <T extends { [K in keyof T]?: string } = {}>(): T => {
    const query = new URLSearchParams(location.search);
    const result: T = {} as T;
    query.forEach((val, key) => {
      result[key] = val;
    });
    return result;
  };

  const { sharedTripHash } = useQuery<{ sharedTripHash: string | undefined }>();

  // "guard" covers PrivateRoute redirects; Login/Register pass their own source.
  const visaSource =
    (location.state as { visaSource?: string })?.visaSource ?? "guard";

  useEffect(() => {
    gtag.googleAnalyticsEvent("verification_modal_shown", {});
    trackEvent("verification_modal_shown", {
      label: MODAL_LABEL,
      metadata: { source: visaSource, returning: readAndMarkModalSeen() },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eligible tiers come from a single comma-separated translation value so each market can localize its own list.
  const tiers = t("visa.auth.modal.tiers")
    .split(",")
    .map(tier => tier.trim())
    .filter(tier => tier !== "");

  const handleNotNow = async () => {
    gtag.googleAnalyticsEvent("verification_cancel_clicked", {});
    trackEvent("verification_cancel_clicked", {
      label: MODAL_LABEL,
      metadata: { action: "not_now" },
    });
    logout();
    removeLocalStorageToken();
    navigate(LANDING.PATH, { replace: true });
  };

  const handleLoginExisting = () => {
    gtag.googleAnalyticsEvent("verification_login_existing_clicked", {});
    trackEvent("verification_cancel_clicked", {
      label: MODAL_LABEL,
      metadata: { action: "login_existing" },
    });
    logout();
    removeLocalStorageToken();
    navigate(LOGIN.PATH, { replace: true });
  };

  const handleContinue = () => {
    gtag.googleAnalyticsEvent("verification_continue_clicked", {});
    trackEvent("verification_continue_clicked", { label: MODAL_LABEL });
    const queryParams = sharedTripHash
      ? `?sharedTripHash=${sharedTripHash}`
      : "";
    const path = `${VISA_AUTH.PATH}${queryParams}`;
    navigate(path, { replace: true });
  };

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <VisaSteps activeStep={2} t={t} />

        <h1 className={classes.title}>{t("visa.auth.modal.title")}</h1>

        <p className={classes.exclusiveTo}>
          {t("visa.auth.modal.exclusiveTo")}
        </p>
        <div className={classes.tiers}>
          {tiers.map(tier => (
            <span key={tier} className={classes.tierChip}>
              {tier}
            </span>
          ))}
        </div>

        <p className={classes.description}>
          {t("visa.auth.modal.description")}
        </p>

        <div className={classes.cardHandy}>
          <b>{t("visa.auth.modal.cardHandyTitle")}</b>{" "}
          {t("visa.auth.modal.cardHandyText")}
        </div>

        <Button
          text={`${t("visa.auth.modal.verifyMyCard")} →`}
          color="primary"
          onClick={handleContinue}
          className={classes.verifyButton}
        />

        <div className={classes.linkRow}>
          <button
            type="button"
            className={classes.loginExistingLink}
            onClick={handleLoginExisting}
          >
            {t("visa.auth.modal.loginExisting")}
          </button>
          <button
            type="button"
            className={classes.notNowLink}
            onClick={handleNotNow}
          >
            {t("visa.auth.modal.notNow")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisaAuthRedirect;
